import { module } from "angular";
import { map } from "rxjs/operators";

import type { Observable } from "rxjs";

import { forEach } from "lodash";

import { SitesRepository } from "App/Modules/Repositories/Site/SitesRepository";
import type { IHttpService } from "App/Modules/Services";
import type {
    ILabel,
    IPartnerUser,
    IPartnerUserFlat,
    IPartnerUserInsert,
    IPartnerUserSite,
    IPartnerUserSlim,
    IResponse,
    ITeamMember,
} from "../../Models";
import type { IPartnerUsersRepository } from "./IPartnerUsersRepository";

export default class PartnerUsersRepository implements IPartnerUsersRepository {
    public static $inject = ["mmHttpService"];

    constructor(private readonly httpService: IHttpService) {}

    public getPartnerUser(partnerId: string, partnerUserId: string): Observable<IResponse<IPartnerUserFlat>> {
        return this.httpService.get<IPartnerUserFlat>(`partners/${partnerId}/users/${partnerUserId}`);
    }

    public search(partnerId: string, search: string): Observable<IResponse<IPartnerUserSlim[]>> {
        return this.httpService.get<IPartnerUserSlim[]>(
            `partners/${partnerId}/users?$search=${encodeURIComponent(search)}`,
        );
    }

    public getPartnerUsersWithRole(partnerId: string): Observable<IResponse<IPartnerUserSlim[]>> {
        return this.httpService.get<IPartnerUserSlim[]>(`partners/${partnerId}/users/withRole`);
    }

    public getPartnersMemberships(partnerId: string, partnerUserId: string): Observable<IResponse<IPartnerUserSlim[]>> {
        return this.httpService.get<IPartnerUser[]>(`partners/${partnerId}/users/${partnerUserId}/partners`);
    }

    public getLabels(partnerId: string): Observable<IResponse<ILabel[]>> {
        return this.httpService.get<ILabel[]>(`partners/${partnerId}/users/labels`);
    }

    public getTeamMemberships(partnerId: string, partnerUserId: string): Observable<IResponse<ITeamMember[]>> {
        return this.httpService.get<ITeamMember[]>(`partners/${partnerId}/users/${partnerUserId}/teams`);
    }

    public getSites(partnerId: string, partnerUserId: string): Observable<IResponse<IPartnerUserSite[]>> {
        return this.httpService.get<IPartnerUserSite[]>(`partners/${partnerId}/users/${partnerUserId}/sites`).pipe(
            map((response: IResponse<IPartnerUserSite[]>) => {
                if (!response.data) {
                    return response;
                }

                forEach(response.data, ({ site }) => SitesRepository.enhanceSite(site));

                return response;
            }),
        );
    }

    public addPartnerUser(partnerId: string, partnerUser: IPartnerUserInsert): Observable<IResponse<IPartnerUserSlim>> {
        return this.httpService.post(`partners/${partnerId}/users/`, partnerUser);
    }

    public deletePartnerUser(partnerId: string, partnerUserId: string): Observable<IResponse<void>> {
        return this.httpService.delete(`partners/${partnerId}/users/${partnerUserId}`);
    }

    public patchPartnerUser(
        partnerId: string,
        partnerUserId: string,
        partnerUserPatch: IPartnerUserFlat,
    ): Observable<IResponse<IPartnerUserFlat>> {
        return this.httpService.patch<IPartnerUserFlat>(
            `partners/${partnerId}/users/${partnerUserId}`,
            partnerUserPatch,
        );
    }

    public verify(
        partnerId: string,
        partnerUserId: string,
        enableTwoFactorAuth: boolean,
    ): Observable<IResponse<IPartnerUser>> {
        return this.httpService.post<IPartnerUser>(
            `partners/${partnerId}/users/${partnerUserId}/verify`,
            enableTwoFactorAuth,
        );
    }
}

module("mmRepositories").service("mmPartnerUsersRepository", PartnerUsersRepository);
